import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Shield, Globe, GraduationCap, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const COGNIA_LOGO = '/cognia_seal.png';

export default function CogniaSection() {
  const { language } = useLanguage();

  const stats = [
    {
      icon: Shield,
      value: '40 000+',
      labelFr: 'Établissements accrédités',
      labelEn: 'Accredited institutions',
    },
    {
      icon: Globe,
      value: '100+',
      labelFr: 'Pays dans le monde',
      labelEn: 'Countries worldwide',
    },
    {
      icon: GraduationCap,
      value: '17M+',
      labelFr: 'Élèves concernés',
      labelEn: 'Students impacted',
    },
  ];

  const points = language === 'fr' ? [
    'Reconnaissance internationale du diplôme',
    'Standards académiques les plus élevés',
    'Accès aux universités américaines et mondiales',
    'Processus d\'amélioration continue certifié',
  ] : [
    'International diploma recognition',
    'Highest academic standards',
    'Access to American and global universities',
    'Certified continuous improvement process',
  ];

  return (
    <section data-testid="cognia-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p
              className="text-[#2D78F6] text-xs font-medium uppercase tracking-[0.2em] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr' ? 'ACCRÉDITATION' : 'ACCREDITATION'}
            </p>
            <h2
              className="text-3xl md:text-4xl font-light text-[#14274B] mb-6 leading-tight"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {language === 'fr'
                ? 'Un diplôme reconnu dans le monde entier'
                : 'A diploma recognized worldwide'}
            </h2>
            <p
              className="text-[#475569] leading-relaxed mb-8"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr'
                ? 'OFA France est accrédité par COGNIA, l\'organisme de référence mondiale en matière d\'accréditation scolaire. Cette accréditation garantit que notre programme répond aux standards académiques les plus exigeants et que le U.S. High School Diploma délivré est reconnu par les universités du monde entier.'
                : 'OFA France is accredited by COGNIA, the global leader in school accreditation. This accreditation ensures that our program meets the highest academic standards and that the U.S. High School Diploma we deliver is recognized by universities worldwide.'}
            </p>
            <ul className="space-y-3">
              {points.map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#66CA9D] flex-shrink-0" />
                  <span className="text-[#14274B] text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {point}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right: Logo + Stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-[#E7EAEF] p-10 text-center mb-8">
              <img
                src={COGNIA_LOGO}
                alt="Accréditation COGNIA — 40 000 établissements dans 100+ pays"
                className="h-28 sm:h-36 w-auto mx-auto mb-6"
                data-testid="cognia-section-logo"
              />
              <p
                className="text-[#14274B] font-semibold text-lg"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {language === 'fr' ? 'Accréditation COGNIA' : 'COGNIA Accreditation'}
              </p>
              <p
                className="text-[#475569] text-sm mt-1"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                NCA CASI · NWAC · SACS CASI
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  className="bg-[#14274B] p-4 text-center"
                  data-testid={`cognia-stat-${index}`}
                >
                  <stat.icon className="w-5 h-5 text-[#2D78F6] mx-auto mb-2" />
                  <p
                    className="text-white font-bold text-xl"
                    style={{ fontFamily: 'Gabarito, sans-serif' }}
                  >
                    {stat.value}
                  </p>
                  <p
                    className="text-white/60 text-xs mt-1"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {language === 'fr' ? stat.labelFr : stat.labelEn}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
